import { Image, type ImageSource } from 'expo-image';
import { Text, View, YStack } from 'tamagui';

import { radius, space } from '@/constants/tokens';
import PSText from './ui/PSText';

export type Testimonial = {
  /** Unbounded prose, up to ~200 words. */
  quote: string;
  name: string;
  designation: string;
  avatar: ImageSource;
};

/**
 * TestimonialNotes — member testimonials as signed paper notes (molecule).
 *
 * The Access Pass landing page's social proof, chosen over three other
 * treatments (a face rail, a rose card carousel, a plain byline list) because
 * it's the only one that speaks the physical-object language the rest of the
 * page already does — the polaroid pile, the circular partner badges. Each
 * quote is a note laid on the dark page with the member's photo clipped over
 * its top corner.
 *
 * There's no height or width constraint on the quote — a long entry just makes
 * a taller note. That's why the column can take entries as lopsided as 25 words
 * and 55 without any of them looking under- or over-filled.
 *
 * Render-only: the screen owns the copy and the list.
 */
const AVATAR = 56;
// The photo hangs half off the note's top edge. The note pads itself by that
// much on top of its own gutter so the quote clears the photo, and the column
// pads by it so the first note's photo isn't clipped by the section above.
const OVERHANG = AVATAR / 2;
const NOTE_PADDING_TOP = OVERHANG + space.lg;
// Laid down, not tossed: fixed angles per position, so a re-render never
// re-rolls them. The polaroid pile's random tilt is right for a pile and wrong
// for a column you read top to bottom.
const TILTS = ['-1.4deg', '1.5deg', '-0.9deg', '1.5deg', '-1.2deg'];

export default function TestimonialNotes({
  title,
  testimonials,
}: {
  title: string;
  testimonials: Testimonial[];
}) {
  return (
    <YStack gap="$lg">
      {/* Light text is hardwired: every section sits on the brandDark landing page. */}
      <PSText.Title color="$textWhite">{title}</PSText.Title>

      <YStack gap="$space.xxxl" pt={OVERHANG}>
        {testimonials.map((testimonial, index) => (
          <YStack
            key={testimonial.name}
            backgroundColor="$color.background"
            rounded="$base"
            px="$lg"
            pb="$lg"
            pt={NOTE_PADDING_TOP}
            gap="$lg"
            rotate={TILTS[index % TILTS.length]}
          >
            {/* The photo sits ON the paper: a white mount ring separates it from
                both the note and the dark page behind. Absolute, so it rides the
                note's tilt instead of pushing the quote down. */}
            <View
              position="absolute"
              top={-OVERHANG}
              left={space.lg}
              p="$xs"
              rounded="$full"
              backgroundColor="$color.background"
            >
              <Image
                source={testimonial.avatar}
                style={{
                  width: AVATAR,
                  height: AVATAR,
                  borderRadius: radius.full,
                }}
                contentFit="cover"
                transition={200}
              />
            </View>

            <Text color="$textDark">{testimonial.quote}</Text>

            <YStack items="flex-end">
              <Text fontSize="$md" color="$color.brand">
                {testimonial.name}
              </Text>
              <Text fontSize="$small" color="$textDarkMuted">
                {testimonial.designation}
              </Text>
            </YStack>
          </YStack>
        ))}
      </YStack>
    </YStack>
  );
}
